import { toast, uuidv4 } from "common";
import { Accessor, createSignal, Setter, Signal } from "solid-js";
import { createStore, produce, SetStoreFunction } from "solid-js/store";
import { queryAssist as api, PreviewBO, PreviewProperty, QueryPreview } from 'st-app/api'

export interface HistoryItem {
    id: string;
    prompt: string;
    preview: QueryPreview;
    isHelpful?: boolean;
}

export class QueryAssistContext {
    loadingPreview: Accessor<boolean>; setLoadingPreview: Setter<boolean>; 
    prompt: Accessor<string>; setPrompt: Setter<string>;
    preview: QueryPreview; setPreview: SetStoreFunction<QueryPreview>;

    history: Accessor<HistoryItem[]>; setHistory: Setter<HistoryItem[]>;
    selectedHistoryId: Accessor<string>; setSelectedHistoryId: Setter<string>;


    constructor(preview?: QueryPreview) {
        [this.preview, this.setPreview] = createStore<QueryPreview>(preview ?? new QueryPreview());
        [this.prompt, this.setPrompt] = createSignal('');
        [this.loadingPreview, this.setLoadingPreview] = createSignal(false);
        [this.history, this.setHistory] = createSignal<HistoryItem[]>([]);
        [this.selectedHistoryId, this.setSelectedHistoryId] = createSignal('');
    }

    reset() {
        this.setPreview(new QueryPreview());
        this.setPrompt('');
        this.setHistory([]);
    }

    async submitPrompt() {
        this.setLoadingPreview(true);

        try {
            const preview = await api.Prompt(this.prompt(), this.preview.stQueryString ?? '');
            const item = { id: uuidv4(), prompt: this.prompt(), preview };
            this.setHistory([...this.history(), item]);
            this.selectHistoryItem(item);
            this.setPrompt('');
        } catch(e) {
            console.error(e);
            toast.notify('error', 'Error submitting prompt');
        } finally {
            this.setLoadingPreview(false);
        }
    }

    selectHistoryItem(item: HistoryItem) {
        this.setSelectedHistoryId(item.id);
        this.setPreview(item.preview);
        this.setPrompt(item.prompt);
    }

    async setUserRating(item: HistoryItem, isHelpful: boolean) {
        if (!item.preview.aiLogGU) return;

        try {
            await api.SetUserRating(item.preview.aiLogGU, isHelpful);

            const history = this.history().map(h => {
                return {
                    ...h,
                    isHelpful: h.id === item.id ? isHelpful : h.isHelpful
                }
            })

            this.setHistory(history);
        } catch(e) {
            console.error(e);
            toast.notify('error', 'Error submitting rating');
        }
    }

    removeProperty(preview: PreviewBO, property: PreviewProperty) {
        this.setPreview(produce(state => {
            const recurse = (bo: PreviewBO) => {
                if (bo.BOID === preview.BOID) {
                    bo.properties = bo.properties.filter(p => p.ID !== property.ID);
                } else {
                    bo.childBOs.forEach(c => recurse(c));
                }
            }

            recurse(state.primaryBO);
        }))
    }

    removeBO(toDelete: PreviewBO) {
        this.setPreview(produce(state => {
            const recurse = (bo: PreviewBO) => {
                if (bo.childBOs.some(c => c.ID === toDelete.ID)) {
                    bo.childBOs = bo.childBOs.filter(c => c.ID !== toDelete.ID);
                } else {
                    for (const child of bo.childBOs) {
                        recurse(child);
                    }
                }
            }

            recurse(state.primaryBO);
        }))
    }
    
}
